/**
 * AIKeyApiService
 *
 * HTTP client for agent-service AI key management endpoints.
 * Communicates with AIKeyController on the backend.
 *
 * @module ai-settings/services
 */

import type { AIProviderType, AIProvidersStatus } from '../types/aiKey.types';
import type { AvailableAIModels } from '../types/aiModel.types';
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';

/** AI endpoints are owned by the agent surface, including split local profiles. */
function getBaseUrl(): string {
  return (
    import.meta.env.VITE_AGENT_API_URL ||
    import.meta.env.VITE_BUILDER_API_URL ||
    'http://localhost:8080'
  );
}

async function handleResponse<T>(response: globalThis.Response): Promise<T> {
  // Check status first so a 404 with an HTML error page (e.g. missing route,
  // SPA fallback) surfaces as "HTTP 404" instead of "Unexpected token '<'".
  if (!response.ok) {
    const ct = response.headers.get('content-type') ?? '';
    const text = await response.text().catch(() => '');
    if (!ct.includes('application/json')) {
      const preview = text.slice(0, 120);
      throw new Error(`HTTP ${response.status} ${response.statusText} (non-JSON body: ${preview})`);
    }
    try {
      const data = JSON.parse(text) as { error?: string };
      throw new Error(data.error || `HTTP ${response.status}`);
    } catch {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
  }
  const data = (await response.json()) as { success?: boolean; error?: string } & T;
  if (data.success === false) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }
  return data as T;
}

async function waitForCurrentUser(timeoutMs = 5000): Promise<User | null> {
  const auth = getAuth();
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | undefined;

    const finish = (user: User | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      unsubscribe?.();
      resolve(user);
    };

    const timeout = window.setTimeout(() => finish(auth.currentUser), timeoutMs);
    unsubscribe = onAuthStateChanged(auth, finish);
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForCurrentUser();
  const token = await user?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export const aiKeyApiService = {
  /**
   * List which providers have configured keys.
   * Returns metadata only — never returns actual key values.
   */
  async listKeys(projectId: string): Promise<AIProvidersStatus> {
    const url = `${getBaseUrl()}/api/ai/keys?projectId=${encodeURIComponent(projectId)}`;
    const response = await fetch(url, {
      headers: await getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean; providers: AIProvidersStatus }>(response);
    return data.providers;
  },

  /**
   * List models whose provider keys can be resolved for this project.
   * Includes user keys and platform-level fallback keys.
   */
  async listModels(projectId: string): Promise<AvailableAIModels> {
    const url = `${getBaseUrl()}/api/ai/models?projectId=${encodeURIComponent(projectId)}`;
    const response = await fetch(url, {
      headers: await getAuthHeaders(),
    });
    const data = await handleResponse<{ success: boolean } & AvailableAIModels>(response);
    return {
      textModels: data.textModels ?? [],
      imageModels: data.imageModels ?? [],
      providers: data.providers ?? {},
    };
  },

  /**
   * Store a user-provided API key.
   * Backend validates the key before storing in Secret Manager.
   */
  async storeKey(projectId: string, provider: AIProviderType, apiKey: string): Promise<void> {
    const url = `${getBaseUrl()}/api/ai/keys`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ projectId, provider, apiKey }),
    });
    await handleResponse(response);
  },

  /**
   * Delete a user-provided API key.
   */
  async deleteKey(projectId: string, provider: AIProviderType): Promise<void> {
    const url = `${getBaseUrl()}/api/ai/keys/${provider}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ projectId }),
    });
    await handleResponse(response);
  },

  /**
   * Validate an API key without storing it.
   */
  async validateKey(provider: AIProviderType, apiKey: string, projectId?: string): Promise<boolean> {
    const url = `${getBaseUrl()}/api/ai/keys/validate`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ projectId, provider, apiKey }),
    });
    const data = await handleResponse<{ success: boolean; valid: boolean }>(response);
    return data.valid;
  },
};
